/**
 * Phase 8 · Cluster C-47 — Team member profile page.
 *
 * Route: `/team/:orgUserId`
 *
 * Renders the full profile for a team member. Every data read passes
 * through `teamMemberProfileService`, which calls
 * `assertCanViewMemberProfile` before hitting Supabase. A single
 * top-level error boundary catches `RbacDenied` and renders
 * `<AccessDeniedState>`; all other errors surface as an inline banner
 * per tab so the header still loads.
 *
 * Tabs:
 *   - Overview    — workload counters + throughput sparklines + TAT
 *                   gauge aggregated across the member's open reqs
 *   - Requisitions — owned requisitions
 *   - Candidates   — candidates created by / assigned to
 *   - Applications — applications currently assigned
 *   - Interviews   — AI interview sessions created
 *   - Emails       — outbound threads sent
 *   - Activity     — raw object_history (real rows, not mocks)
 *
 * Caching: each query uses a React Query key that embeds the
 * viewer.orgUserId. Flipping orgs or re-logging as a different user
 * wipes the cache automatically — we never share decisions across
 * viewers. See queryKeys.teamMemberProfile.*.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ClipboardList,
  Users,
  FileText,
  CalendarCheck,
  Mail,
  Activity as ActivityIcon,
  AlertTriangle,
  Loader2,
  Briefcase,
  TrendingUp,
  UserCircle2,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import {
  isRbacDenied,
  viewerFromAuthUser,
} from '@core/auth/rbac';
import {
  teamMemberProfileService,
  type MemberProfile,
} from '@core/supabase/services/teamMemberProfileService';
import { queryKeys } from '@core/query/queryKeys';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { Sparkline } from '@shared/components/ui/Sparkline';
import { StatusBadge } from '@shared/components/ui/StatusBadge';
import { AccessDeniedState } from '@shared/components/ui/AccessDeniedState';

// ──────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────

type TabKey =
  | 'overview'
  | 'requisitions'
  | 'candidates'
  | 'applications'
  | 'interviews'
  | 'emails'
  | 'activity';

const TABS: Array<{ key: TabKey; label: string; icon: typeof ClipboardList }> = [
  { key: 'overview', label: 'Overview', icon: TrendingUp },
  { key: 'requisitions', label: 'Requisitions', icon: ClipboardList },
  { key: 'candidates', label: 'Candidates', icon: Users },
  { key: 'applications', label: 'Applications', icon: FileText },
  { key: 'interviews', label: 'Interviews', icon: CalendarCheck },
  { key: 'emails', label: 'Emails', icon: Mail },
  { key: 'activity', label: 'Activity', icon: ActivityIcon },
];

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function roleLabel(role: string | null | undefined): string {
  switch (role) {
    case 'owner': return 'Owner';
    case 'admin': return 'Admin';
    case 'senior_recruiter': return 'Senior Recruiter';
    case 'junior_recruiter': return 'Junior Recruiter';
    case 'viewer': return 'Viewer';
    default: return role ?? 'Member';
  }
}

// ──────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────

export function ProfilePage() {
  const { orgUserId = '' } = useParams<{ orgUserId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const viewer = useMemo(() => viewerFromAuthUser(user), [user]);
  const orgId = viewer.orgId ?? '';
  const viewerId = viewer.orgUserId ?? '';
  const [tab, setTab] = useState<TabKey>('overview');

  const profileQuery = useQuery({
    queryKey: queryKeys.teamMemberProfile.profile(orgId, viewerId, orgUserId),
    queryFn: () =>
      teamMemberProfileService.getProfile({ viewer, targetUserId: orgUserId }),
    enabled: Boolean(orgId && viewerId && orgUserId),
    staleTime: 30_000,
    retry: (failureCount, err) => !isRbacDenied(err) && failureCount < 2,
    refetchOnWindowFocus: false,
  });

  if (!orgId || !viewerId) {
    return <FullPageSpinner />;
  }

  if (profileQuery.isLoading) {
    return <FullPageSpinner />;
  }

  if (profileQuery.isError) {
    if (isRbacDenied(profileQuery.error)) {
      return (
        <div className="pt-10">
          <AccessDeniedState />
        </div>
      );
    }
    return (
      <div className="pt-10">
        <AccessDeniedState
          message={
            profileQuery.error instanceof Error
              ? profileQuery.error.message
              : 'Could not load this profile.'
          }
          backLabel="Back to team"
        />
      </div>
    );
  }

  const profile = profileQuery.data;
  if (!profile) {
    return <FullPageSpinner />;
  }

  return (
    <div>
      <PageHeader
        breadcrumb={[
          { label: 'Organization', href: '/settings/organisation' },
          { label: 'Team', href: '/team' },
          { label: profile.user.display_name ?? profile.user.email },
        ]}
        title={profile.user.display_name ?? profile.user.email}
        subtitle={
          <span className="inline-flex items-center gap-2 text-[13px] vscode-text-secondary">
            <UserCircle2 className="h-3.5 w-3.5" aria-hidden />
            {roleLabel(profile.user.role)}
          </span>
        }
        action={
          <button
            type="button"
            onClick={() => navigate('/team')}
            className="btn-secondary flex items-center gap-1.5 text-sm"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to team
          </button>
        }
      />

      <MemberProfileHeader profile={profile} />

      {/* Tabs */}
      <div className="mt-6 border-b vscode-border">
        <nav className="-mb-px flex flex-wrap gap-1" role="tablist" aria-label="Profile sections">
          {TABS.map(({ key, label, icon: Icon }) => {
            const active = key === tab;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-[12px] font-medium transition-colors ${
                  active
                    ? 'border-[var(--vscode-text-link)] text-[var(--vscode-text-link)]'
                    : 'border-transparent vscode-text-secondary hover:vscode-text-primary'
                }`}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="pt-4">
        {tab === 'overview' && <OverviewTab profile={profile} viewer={viewer} orgId={orgId} viewerId={viewerId} />}
        {tab === 'requisitions' && <RequisitionsTab targetId={orgUserId} viewer={viewer} orgId={orgId} viewerId={viewerId} />}
        {tab === 'candidates' && <CandidatesTab targetId={orgUserId} viewer={viewer} orgId={orgId} viewerId={viewerId} />}
        {tab === 'applications' && <ApplicationsTab targetId={orgUserId} viewer={viewer} orgId={orgId} viewerId={viewerId} />}
        {tab === 'interviews' && <InterviewsTab targetId={orgUserId} viewer={viewer} orgId={orgId} viewerId={viewerId} />}
        {tab === 'emails' && <EmailsTab targetId={orgUserId} viewer={viewer} orgId={orgId} viewerId={viewerId} />}
        {tab === 'activity' && <ActivityTab targetId={orgUserId} viewer={viewer} orgId={orgId} viewerId={viewerId} />}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Header — avatar / name / manager / direct reports
// ──────────────────────────────────────────────────────────────────

function MemberProfileHeader({ profile }: { profile: MemberProfile }) {
  const { user, manager, directReports, currentWorkload } = profile;
  return (
    <div className="rounded-xl border vscode-border vscode-bg-secondary p-4">
      <div className="flex flex-wrap items-start gap-4">
        <TableAvatar
          name={user.display_name ?? user.email}
          size={56}
          src={user.avatar_url ?? undefined}
        />

        <div className="min-w-0 flex-1">
          <p className="truncate text-[16px] font-semibold vscode-text-primary">
            {user.display_name ?? user.email}
          </p>
          <p className="truncate text-[12px] vscode-text-tertiary">{user.email}</p>
          <p className="mt-1 inline-flex items-center gap-1 text-[12px] vscode-text-secondary">
            <Briefcase className="h-3 w-3" aria-hidden /> {roleLabel(user.role)}
          </p>

          {manager && (
            <p className="mt-1 text-[12px] vscode-text-tertiary">
              Reports to{' '}
              <Link
                to={`/team/${manager.id}`}
                className="font-medium text-[var(--vscode-text-link)] hover:underline"
              >
                {manager.display_name ?? manager.email}
              </Link>
            </p>
          )}
        </div>

        {/* Direct reports stack */}
        {directReports.length > 0 && (
          <div className="flex shrink-0 flex-col items-end">
            <p className="mb-1 text-[11px] uppercase tracking-wider vscode-text-tertiary">
              Direct reports
            </p>
            <ul className="flex items-center -space-x-1.5">
              {directReports.slice(0, 6).map((r) => (
                <li key={r.id} className="rounded-full ring-2 ring-[var(--vscode-bg-secondary)]">
                  <Link to={`/team/${r.id}`} title={r.display_name ?? r.email ?? ''}>
                    <TableAvatar name={r.display_name ?? r.email ?? '?'} size={28} src={r.avatar_url ?? undefined} />
                  </Link>
                </li>
              ))}
              {directReports.length > 6 && (
                <li className="flex h-7 w-7 items-center justify-center rounded-full border vscode-border vscode-bg-tertiary text-[10px] font-semibold vscode-text-secondary ring-2 ring-[var(--vscode-bg-secondary)]">
                  +{directReports.length - 6}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Workload counters */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <WorkloadCell
          label="Open requisitions"
          value={currentWorkload.openReqs}
          accent="emerald"
          icon={ClipboardList}
        />
        <WorkloadCell
          label="Active pipelines"
          value={currentWorkload.activePipelines}
          accent="sky"
          icon={FileText}
        />
        <WorkloadCell
          label="Direct reports"
          value={directReports.length}
          accent="violet"
          icon={Users}
        />
        <WorkloadCell
          label="Member since"
          value={user.joined_at ? new Date(user.joined_at).toLocaleDateString() : '—'}
          icon={CalendarCheck}
        />
      </div>
    </div>
  );
}

function WorkloadCell({
  label,
  value,
  accent,
  icon: Icon,
}: {
  label: string;
  value: number | string;
  accent?: 'emerald' | 'sky' | 'amber' | 'violet';
  icon?: typeof ClipboardList;
}) {
  const accentClasses = {
    emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    sky: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  };
  const colorClass = accent ? accentClasses[accent] : 'vscode-text-primary';

  return (
    <div className="rounded-xl border vscode-border vscode-bg-primary px-4 py-3">
      <div className="flex items-center gap-2 mb-1">
        {Icon ? (
          <div className={`flex h-6 w-6 items-center justify-center rounded-lg ${accent ? accentClasses[accent] : 'vscode-bg-tertiary'}`}>
            <Icon className="h-3.5 w-3.5" />
          </div>
        ) : null}
        <p className="text-[10px] uppercase tracking-wider vscode-text-tertiary">{label}</p>
      </div>
      <p className={`text-[20px] font-semibold ${colorClass}`}>{value}</p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Overview — throughput sparkline grid + TAT gauge
// ──────────────────────────────────────────────────────────────────

interface TabProps {
  targetId: string;
  viewer: ReturnType<typeof viewerFromAuthUser>;
  orgId: string;
  viewerId: string;
}

function OverviewTab({
  profile,
  viewer,
  orgId,
  viewerId,
}: {
  profile: MemberProfile;
  viewer: ReturnType<typeof viewerFromAuthUser>;
  orgId: string;
  viewerId: string;
}) {
  const targetId = profile.user.id;
  const throughputQuery = useQuery({
    queryKey: queryKeys.teamMemberProfile.throughput(orgId, viewerId, targetId),
    queryFn: () =>
      teamMemberProfileService.getThroughput30d({ viewer, targetUserId: targetId }),
    enabled: Boolean(orgId && viewerId && targetId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary">
          Throughput · last 14 days
        </h3>
        {throughputQuery.isLoading ? (
          <div className="rounded-[8px] border vscode-border vscode-bg-secondary p-6 text-center">
            <Loader2 className="mx-auto h-4 w-4 animate-spin vscode-text-tertiary" />
          </div>
        ) : throughputQuery.isError ? (
          <ErrorBanner message="Could not load throughput." />
        ) : (
          <ThroughputSparklineGrid data={throughputQuery.data!} />
        )}
      </div>
      <div>
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary">
          TAT gauge
        </h3>
        <TatGauge openReqs={profile.currentWorkload.openReqs} />
      </div>
    </div>
  );
}

function ThroughputSparklineGrid({
  data,
}: {
  data: import('@core/supabase/services/teamMemberProfileService').MemberThroughput30d;
}) {
  const cards: Array<{ label: string; values: number[]; accent: string; bg: string; icon: typeof Users }> = [
    {
      label: 'Candidates touched',
      values: data.candidatesTouched,
      accent: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-500/10',
      icon: Users,
    },
    {
      label: 'Interviews run',
      values: data.interviewsRun,
      accent: 'text-sky-600 dark:text-sky-400',
      bg: 'bg-sky-500/10',
      icon: CalendarCheck,
    },
    {
      label: 'Emails sent',
      values: data.emailsSent,
      accent: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-500/10',
      icon: Mail,
    },
    {
      label: 'Offers issued',
      values: data.offersIssued,
      accent: 'text-violet-600 dark:text-violet-400',
      bg: 'bg-violet-500/10',
      icon: Briefcase,
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {cards.map((c) => {
        const total = c.values.reduce((s, v) => s + v, 0);
        const Icon = c.icon;
        return (
          <div
            key={c.label}
            className="rounded-xl border vscode-border vscode-bg-secondary p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${c.bg}`}>
                <Icon className={`h-4 w-4 ${c.accent}`} />
              </div>
              <p className="text-[11px] uppercase tracking-wider vscode-text-tertiary">{c.label}</p>
            </div>
            <div className="flex items-end justify-between gap-3">
              <p className={`text-[24px] font-bold ${c.accent}`}>{total}</p>
              <div className="flex-1 max-w-[140px]">
                <Sparkline values={c.values} width={140} height={36} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Lightweight TAT gauge with visual indicator.
 */
function TatGauge({ openReqs }: { openReqs: number }) {
  const tone =
    openReqs === 0
      ? { text: 'vscode-text-tertiary', bg: 'vscode-bg-tertiary', bar: 'bg-gray-400' }
      : openReqs < 3
        ? { text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10', bar: 'bg-emerald-500' }
        : openReqs < 6
          ? { text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10', bar: 'bg-amber-500' }
          : { text: 'text-red-500', bg: 'bg-red-500/10', bar: 'bg-red-500' };

  const pct = Math.min(100, openReqs * 12);

  return (
    <div className="flex h-full flex-col rounded-xl border vscode-border vscode-bg-secondary p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-wider vscode-text-tertiary">
          Open reqs in flight
        </p>
        <div className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${tone.bg} ${tone.text}`}>
          {openReqs === 0
            ? 'Clear'
            : openReqs < 3
              ? 'Healthy'
              : openReqs < 6
                ? 'Busy'
                : 'At capacity'}
        </div>
      </div>
      <p className={`text-[36px] font-bold leading-tight ${tone.text}`}>{openReqs}</p>

      {/* Visual gauge bar */}
      <div className="mt-3 h-2 w-full rounded-full vscode-bg-tertiary overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${tone.bar}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <p className="mt-2 text-[11px] vscode-text-tertiary">
        {openReqs === 0
          ? 'No open requisitions owned.'
          : openReqs < 3
            ? 'Capacity to take more positions.'
            : openReqs < 6
              ? 'Approaching full capacity.'
              : 'Consider redistributing work.'}
      </p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab: Requisitions
// ──────────────────────────────────────────────────────────────────

function RequisitionsTab({ targetId, viewer, orgId, viewerId }: TabProps) {
  const q = useQuery({
    queryKey: queryKeys.teamMemberProfile.requisitions(orgId, viewerId, targetId),
    queryFn: () =>
      teamMemberProfileService.getRequisitions({ viewer, targetUserId: targetId }),
    enabled: Boolean(orgId && viewerId && targetId),
    staleTime: 30_000,
  });
  if (q.isLoading) return <TabLoading />;
  if (q.isError) return <ErrorBanner message="Could not load requisitions." />;
  const rows = q.data ?? [];
  if (rows.length === 0) return <EmptyTab label="No requisitions owned." />;
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li
          key={r.id}
          className="rounded-[8px] border vscode-border vscode-bg-secondary px-3 py-2"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium vscode-text-primary">{r.title}</p>
              <p className="text-[11px] vscode-text-tertiary">
                {r.status} · {r.priority ?? 'normal'} · HC {r.headcount ?? 1}
              </p>
            </div>
            <Link
              to={`/requisitions/${r.id}`}
              className="shrink-0 rounded-[6px] border px-2 py-1 text-[11px] font-medium vscode-border vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover"
            >
              Open →
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab: Candidates
// ──────────────────────────────────────────────────────────────────

function CandidatesTab({ targetId, viewer, orgId, viewerId }: TabProps) {
  const q = useQuery({
    queryKey: queryKeys.teamMemberProfile.candidates(orgId, viewerId, targetId, 100),
    queryFn: () =>
      teamMemberProfileService.getCandidates({ viewer, targetUserId: targetId }, { limit: 100 }),
    enabled: Boolean(orgId && viewerId && targetId),
    staleTime: 30_000,
  });
  if (q.isLoading) return <TabLoading />;
  if (q.isError) return <ErrorBanner message="Could not load candidates." />;
  const rows = q.data ?? [];
  if (rows.length === 0) return <EmptyTab label="No candidates created or assigned." />;
  return (
    <ul className="space-y-2">
      {rows.map((c) => (
        <li
          key={c.id}
          className="flex items-center justify-between gap-3 rounded-[8px] border vscode-border vscode-bg-secondary px-3 py-2"
        >
          <div className="min-w-0">
            <p className="truncate text-[13px] font-medium vscode-text-primary">
              {c.full_name ?? c.email ?? 'Unnamed candidate'}
            </p>
            <p className="text-[11px] vscode-text-tertiary">
              {c.source ?? '—'} · {c.match}
            </p>
          </div>
          <Link
            to={`/candidates/${c.id}`}
            className="shrink-0 rounded-[6px] border px-2 py-1 text-[11px] font-medium vscode-border vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover"
          >
            Open →
          </Link>
        </li>
      ))}
    </ul>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab: Applications
// ──────────────────────────────────────────────────────────────────

function ApplicationsTab({ targetId, viewer, orgId, viewerId }: TabProps) {
  const q = useQuery({
    queryKey: queryKeys.teamMemberProfile.applications(orgId, viewerId, targetId),
    queryFn: () =>
      teamMemberProfileService.getApplications({ viewer, targetUserId: targetId }),
    enabled: Boolean(orgId && viewerId && targetId),
    staleTime: 30_000,
  });
  if (q.isLoading) return <TabLoading />;
  if (q.isError) return <ErrorBanner message="Could not load applications." />;
  const rows = q.data ?? [];
  if (rows.length === 0) return <EmptyTab label="No applications assigned." />;
  return (
    <ul className="space-y-2">
      {rows.map((a) => (
        <li
          key={a.id}
          className="flex items-center justify-between gap-3 rounded-[8px] border vscode-border vscode-bg-secondary px-3 py-2"
        >
          <div className="min-w-0">
            <p className="truncate text-[13px] vscode-text-primary">{a.id.slice(0, 8)}…</p>
            <p className="text-[11px] vscode-text-tertiary">
              Updated {formatRelative(a.status_updated_at)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={(a.status as string) ?? 'submitted'} />
            <Link
              to={`/applications/${a.id}`}
              className="shrink-0 rounded-[6px] border px-2 py-1 text-[11px] font-medium vscode-border vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover"
            >
              Open →
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab: Interviews
// ──────────────────────────────────────────────────────────────────

function InterviewsTab({ targetId, viewer, orgId, viewerId }: TabProps) {
  const q = useQuery({
    queryKey: queryKeys.teamMemberProfile.interviews(orgId, viewerId, targetId),
    queryFn: () =>
      teamMemberProfileService.getInterviews({ viewer, targetUserId: targetId }),
    enabled: Boolean(orgId && viewerId && targetId),
    staleTime: 30_000,
  });
  if (q.isLoading) return <TabLoading />;
  if (q.isError) return <ErrorBanner message="Could not load interviews." />;
  const rows = q.data ?? [];
  if (rows.length === 0) return <EmptyTab label="No interview sessions." />;
  return (
    <ul className="space-y-2">
      {rows.map((i) => (
        <li
          key={i.id}
          className="flex items-center justify-between gap-3 rounded-[8px] border vscode-border vscode-bg-secondary px-3 py-2"
        >
          <div className="min-w-0">
            <p className="truncate text-[13px] vscode-text-primary">Session {i.id.slice(0, 8)}…</p>
            <p className="text-[11px] vscode-text-tertiary">
              {i.status ?? '—'} · {formatRelative(i.created_at)}
            </p>
          </div>
          <Link
            to={`/ai-interviews/${i.id}`}
            className="shrink-0 rounded-[6px] border px-2 py-1 text-[11px] font-medium vscode-border vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover"
          >
            Open →
          </Link>
        </li>
      ))}
    </ul>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab: Emails
// ──────────────────────────────────────────────────────────────────

function EmailsTab({ targetId, viewer, orgId, viewerId }: TabProps) {
  const q = useQuery({
    queryKey: queryKeys.teamMemberProfile.emails(orgId, viewerId, targetId, 50),
    queryFn: () =>
      teamMemberProfileService.getEmails({ viewer, targetUserId: targetId }, { limit: 50 }),
    enabled: Boolean(orgId && viewerId && targetId),
    staleTime: 30_000,
  });
  if (q.isLoading) return <TabLoading />;
  if (q.isError) return <ErrorBanner message="Could not load emails." />;
  const rows = q.data ?? [];
  if (rows.length === 0) return <EmptyTab label="No outbound emails sent." />;
  return (
    <ul className="space-y-2">
      {rows.map((e) => (
        <li
          key={e.id}
          className="rounded-[8px] border vscode-border vscode-bg-secondary px-3 py-2"
        >
          <p className="truncate text-[13px] font-medium vscode-text-primary">
            {e.subject ?? '(no subject)'}
          </p>
          <p className="text-[11px] vscode-text-tertiary">
            to {e.to_email ?? '—'} · {formatRelative(e.sent_at)}
            {e.cta_response ? ` · replied: ${e.cta_response}` : ''}
          </p>
        </li>
      ))}
    </ul>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab: Activity
// ──────────────────────────────────────────────────────────────────

function ActivityTab({ targetId, viewer, orgId, viewerId }: TabProps) {
  const q = useQuery({
    queryKey: queryKeys.teamMemberProfile.activity(orgId, viewerId, targetId, 50),
    queryFn: () =>
      teamMemberProfileService.getActivity({ viewer, targetUserId: targetId }, { limit: 50 }),
    enabled: Boolean(orgId && viewerId && targetId),
    staleTime: 15_000,
  });
  if (q.isLoading) return <TabLoading />;
  if (q.isError) return <ErrorBanner message="Could not load activity." />;
  const rows = q.data ?? [];
  if (rows.length === 0) return <EmptyTab label="No audit events recorded for this member." />;
  return (
    <ol className="space-y-2">
      {rows.map((ev) => (
        <li
          key={ev.id}
          className="rounded-[8px] border vscode-border vscode-bg-secondary px-3 py-2"
        >
          <p className="text-[12px] vscode-text-primary">
            <span className="font-mono text-[11px] vscode-text-secondary">{ev.entity_type}</span>{' '}
            <span className="vscode-text-secondary">{ev.action}</span>
            {ev.attribute_name ? (
              <>
                {' '}
                · <span className="font-mono text-[11px]">{ev.attribute_name}</span>
              </>
            ) : null}
          </p>
          {(ev.old_value || ev.new_value) && (
            <p className="mt-0.5 font-mono text-[11px] vscode-text-tertiary">
              <span className="text-red-400 line-through">{ev.old_value ?? '—'}</span>
              {' → '}
              <span className="text-emerald-400">{ev.new_value ?? '—'}</span>
            </p>
          )}
          <p className="mt-0.5 text-[11px] vscode-text-tertiary">{formatRelative(ev.created_at)}</p>
        </li>
      ))}
    </ol>
  );
}

// ──────────────────────────────────────────────────────────────────
// Shared tab states
// ──────────────────────────────────────────────────────────────────

function TabLoading() {
  return (
    <div className="flex items-center gap-2 text-[12px] vscode-text-tertiary">
      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      Loading…
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <p className="inline-flex items-center gap-2 rounded-[8px] border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-700 dark:text-amber-300">
      <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
      {message}
    </p>
  );
}

function EmptyTab({ label }: { label: string }) {
  return <p className="text-[12px] vscode-text-tertiary">{label}</p>;
}
